import {
  Document as DocxDocument,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
} from 'docx';
import {
  Document as PdfDocument,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';

export interface AgentBuilderArtifactSection {
  heading: string;
  content: string;
}

export interface AgentBuilderArtifact {
  title: string;
  subtitle: string;
  sections: AgentBuilderArtifactSection[];
}

const pdfStyles = StyleSheet.create({
  page: { padding: 44, fontFamily: 'Helvetica', color: '#182033', fontSize: 10.5, lineHeight: 1.55 },
  eyebrow: { color: '#1d4f91', fontSize: 9, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase' },
  title: { fontSize: 23, fontWeight: 700, marginBottom: 8 },
  subtitle: { color: '#5c667a', fontSize: 11, marginBottom: 24 },
  section: { borderTopWidth: 1, borderTopColor: '#d9deea', paddingTop: 14, marginTop: 12 },
  heading: { color: '#1d4f91', fontSize: 13, fontWeight: 700, marginBottom: 7 },
  content: { color: '#2f3747', whiteSpace: 'pre-wrap' },
  footer: { position: 'absolute', left: 44, right: 44, bottom: 24, color: '#7a8497', fontSize: 8 },
});

function safeFilename(value: string): string {
  return value
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'agent-builder-document';
}

function AgentArtifactPdf({ artifact }: { artifact: AgentBuilderArtifact }) {
  return (
    <PdfDocument title={artifact.title} author="MEDD-SIM">
      <Page size="A4" style={pdfStyles.page}>
        <Text style={pdfStyles.eyebrow}>Simulation design document</Text>
        <Text style={pdfStyles.title}>{artifact.title}</Text>
        <Text style={pdfStyles.subtitle}>{artifact.subtitle}</Text>
        {artifact.sections.map((section, index) => (
          <View key={`${section.heading}-${index}`} style={pdfStyles.section} wrap={false}>
            <Text style={pdfStyles.heading}>{section.heading}</Text>
            <Text style={pdfStyles.content}>{section.content}</Text>
          </View>
        ))}
        <Text style={pdfStyles.footer} fixed>
          Generated for administrator review. Verify factual claims before using this document in a live simulation.
        </Text>
      </Page>
    </PdfDocument>
  );
}

export async function downloadAgentArtifactPdf(artifact: AgentBuilderArtifact): Promise<void> {
  const blob = await pdf(<AgentArtifactPdf artifact={artifact} />).toBlob();
  saveAs(blob, `${safeFilename(artifact.title)}.pdf`);
}

export async function downloadAgentArtifactDocx(artifact: AgentBuilderArtifact): Promise<void> {
  const children = [
    new Paragraph({
      text: artifact.title,
      heading: HeadingLevel.TITLE,
      spacing: { after: 160 },
    }),
    new Paragraph({
      children: [new TextRun({ text: artifact.subtitle, color: '5C667A', italics: true })],
      spacing: { after: 360 },
    }),
  ];

  for (const section of artifact.sections) {
    children.push(
      new Paragraph({
        text: section.heading,
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 260, after: 120 },
      }),
      ...section.content
        .split(/\n+/)
        .map((line) => new Paragraph({ text: line.trim(), spacing: { after: 100 } }))
        .filter((paragraph) => paragraph),
    );
  }

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Generated for administrator review. Verify factual claims before using this document in a live simulation.',
          color: '7A8497',
          italics: true,
          size: 18,
        }),
      ],
      spacing: { before: 360 },
    }),
  );

  const document = new DocxDocument({ sections: [{ children }] });
  const blob = await Packer.toBlob(document);
  saveAs(blob, `${safeFilename(artifact.title)}.docx`);
}
